import React, { useEffect, useState } from 'react';
import Select from 'react-select-legacy';
import PropTypes from 'prop-types';
import Routes from '../../utils/routes.js.erb';
import { authHeaders } from '../../actions/index';
import ReactPolling from 'react-polling';
import moment from 'moment-timezone';
import localeNames from '../../utils/localeNames.jsx.erb';

export const LastFollowUpParentMessageSendout = ({ survey }) => {
  const [lastSendout, setLastSendout] = useState(null);

  return (
    <ReactPolling
      url={Routes.follow_up_parent_message_sendout_api_dashboard_asq_survey_path(
        survey.id,
      )}
      interval={5000}
      onSuccess={(response) => {
        setLastSendout(response.sent_at);
      }}
      method={'GET'}
      headers={{
        'Content-Type': 'application/json',
        ...authHeaders(),
      }}
      render={() => {
        if (!lastSendout) {
          return <></>;
        }

        return (
          <div className="c-locale-legend">
            Last Sent: <span>{moment(lastSendout).format('ll')}</span>
          </div>
        );
      }}
    />
  );
};

LastFollowUpParentMessageSendout.defaultProps = {
  survey: null,
};

LastFollowUpParentMessageSendout.propTypes = {
  survey: PropTypes.object.isRequired,
};

export const FollowUpParentMessage = ({ survey, defaultLocale, onSent, onTranslationStateChange }) => {
  const [templates, setTemplates] = useState([]);
  const [locale, setLocale] = useState(defaultLocale || 'en');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [message, setMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [translationSuccess, setTranslationSuccess] = useState(null);
  const [originalMessage, setOriginalMessage] = useState(null);
  const [targetTranslateLocale, setTargetTranslateLocale] = useState(defaultLocale || 'en');
  const [isFallback, setIsFallback] = useState(false);
  const [includeFollowUpAnswers, setIncludeFollowUpAnswers] = useState(false);
  const [followUpAnswers, setFollowUpAnswers] = useState('');
  const [isLoadingFollowUpAnswers, setIsLoadingFollowUpAnswers] = useState(false);

  const localeOptions = Object.entries(localeNames).map(([k, v]) => ({
    value: k,
    label: v,
  }));

  const localeOptionRenderer = (opt) => {
    return opt.value === defaultLocale ? (
      <span>{opt.label} <span role="img" aria-label="check">✅</span></span>
    ) : opt.label;
  };

  const fetchTemplates = async (selectedLocale) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        Routes.follow_up_parent_message_templates_api_dashboard_asq_survey_path(
          survey.id,
          { locale: selectedLocale },
        ),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
        },
      );
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFollowUpAnswers = async (targetLocale) => {
    setIsLoadingFollowUpAnswers(true);
    try {
      const response = await fetch(
        Routes.follow_up_answers_for_message_api_dashboard_asq_survey_path(
          survey.id,
          { locale: targetLocale },
        ),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
        },
      );
      const data = await response.json();
      setFollowUpAnswers(data.formatted_answers || '');
    } catch (error) {
      console.error('Error fetching follow-up answers:', error);
      setFollowUpAnswers('');
    } finally {
      setIsLoadingFollowUpAnswers(false);
    }
  };

  useEffect(() => {
    fetchTemplates(locale);
  }, [survey.id]);

  useEffect(() => {
    if (includeFollowUpAnswers) {
      fetchFollowUpAnswers(locale);
    }
  }, [includeFollowUpAnswers, survey.id, locale]);

  const onLocaleChange = async (selectedOption) => {
    const newLocale = selectedOption.value;
    setLocale(newLocale);
    setTranslationSuccess(null);

    // Fetch templates for the new locale
    setIsLoading(true);
    try {
      const response = await fetch(
        Routes.follow_up_parent_message_templates_api_dashboard_asq_survey_path(
          survey.id,
          { locale: newLocale },
        ),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
        },
      );
      const data = await response.json();
      setTemplates(data);

      // If a template was selected, update its content with the translated version
      if (selectedMessage) {
        const updatedTemplate = data.find((t) => t.id === selectedMessage.value);
        if (updatedTemplate) {
          const newSelectedMessage = {
            value: updatedTemplate.id,
            label: updatedTemplate.title,
            message: updatedTemplate.message,
            isFallback: updatedTemplate.is_fallback,
          };
          setSelectedMessage(newSelectedMessage);
          setMessage(updatedTemplate.message);
          setOriginalMessage(updatedTemplate.message);
          setHasUnsavedChanges(false);
          setIsFallback(updatedTemplate.is_fallback);
        }
      } else {
        setIsFallback(false);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const translateMessage = async (targetLocale) => {
    if (!message || !targetLocale) return;

    setIsTranslating(true);
    setTranslationSuccess(null);
    setSendError(null);

    const targetLocaleLabel = localeNames[targetLocale];

    try {
      // Translate message
      const messageResponse = await fetch(
        Routes.translate_follow_up_parent_message_api_dashboard_asq_survey_path(survey.id),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
          body: JSON.stringify({
            text: message,
            target_locale: targetLocale,
          }),
        },
      );

      const messageData = await messageResponse.json();

      if (messageData.error) {
        setSendError(messageData.error);
        return;
      }

      if (messageData.translated_text) {
        setMessage(messageData.translated_text);
        setHasUnsavedChanges(true);
        setIsFallback(false);
      }

      // Translate follow-up answers if checkbox is checked
      if (includeFollowUpAnswers && followUpAnswers) {
        const answersResponse = await fetch(
          Routes.translate_follow_up_parent_message_api_dashboard_asq_survey_path(survey.id),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders(),
            },
            body: JSON.stringify({
              text: followUpAnswers,
              target_locale: targetLocale,
            }),
          },
        );

        const answersData = await answersResponse.json();

        if (answersData.translated_text) {
          setFollowUpAnswers(answersData.translated_text);
        }
      }

      setTranslationSuccess(
        `The content has been translated to ${targetLocaleLabel} using AI. Please review it and click "Send" when ready.`
      );
    } catch (error) {
      console.error('Error translating message:', error);
      setSendError('Translation failed. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  };

  const send = async () => {
    if (!window.confirm('Are you sure you want to send this message?')) {
      return;
    }

    try {
      setIsSending(true);
      setSendError(null);

      const finalMessage = includeFollowUpAnswers && followUpAnswers
        ? `${message}\n\n---\n\n${followUpAnswers}`
        : message;

      const data = await fetch(
        Routes.follow_up_parent_message_sendout_api_dashboard_asq_survey_path(
          survey.id,
        ),
        {
          method: 'POST',
          body: JSON.stringify({
            parent_message_id: selectedMessage.value,
            message: finalMessage,
          }),
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
        },
      );

      setHasUnsavedChanges(false);
      onSent(data);
    } catch (e) {
      setSendError(e.response?.data?.error || 'Something went wrong');
    } finally {
      setIsSending(false);
    }
  };

  // Notify parent when unsaved changes state changes
  useEffect(() => {
    if (onTranslationStateChange) {
      onTranslationStateChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onTranslationStateChange]);

  // Track message changes to detect unsaved edits
  useEffect(() => {
    if (originalMessage !== null && message !== originalMessage) {
      setHasUnsavedChanges(true);
    }
  }, [message, originalMessage]);

  useEffect(() => {
    if (selectedMessage) {
      setMessage(selectedMessage.message);
      setOriginalMessage(selectedMessage.message);
      setHasUnsavedChanges(false);
      setTranslationSuccess(null);
    }
  }, [selectedMessage]);

  return (
    <div className="o-parent-message-modal">
      <h3>
        <p className="title">Follow-up Parent Message</p>
        <p className="subtitle">
          Please select the message you would like to send to the parent.
        </p>
      </h3>

      {sendError && (
        <div className="bar error" style={{ margin: '20px 0' }}>
          {sendError}
        </div>
      )}

      {translationSuccess && (
        <div
          className="bar success"
          style={{
            margin: '20px 0',
            padding: '10px 15px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
          }}
        >
          {translationSuccess}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Template Language
        </label>
        <Select
          value={locale}
          options={localeOptions}
          onChange={onLocaleChange}
          clearable={false}
          disabled={isLoading || isSending || isTranslating}
        />
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading templates...
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Template
            </label>
            <Select
              value={selectedMessage}
              options={templates.map((t) => ({
                value: t.id,
                label: t.title,
                message: t.message,
                isFallback: t.is_fallback,
              }))}
              onChange={(selectedOption) => {
                setSelectedMessage(selectedOption);
                setIsFallback(selectedOption?.isFallback || false);
              }}
              disabled={isSending || isTranslating}
            />
          </div>
          <div style={{ flex: 1, marginBottom: '20px', display: 'flex', flexDirection: 'column' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Message
            </label>
            {isFallback && locale !== 'en' && (
              <div
                style={{
                  marginBottom: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '4px',
                  color: '#856404',
                  fontSize: '14px',
                }}
              >
                This template doesn&apos;t have a translation for {localeNames[locale]}. Showing English content.
              </div>
            )}
            <textarea
              style={{
                width: '100%',
                flex: 1,
                minHeight: '150px',
                resize: 'none',
              }}
              onChange={(e) => {
                setMessage(e.target.value);
                setTranslationSuccess(null);
              }}
              value={message || ''}
              disabled={isTranslating}
            />
          </div>

          {/* Include Follow-Up Form Answers Checkbox */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeFollowUpAnswers}
                onChange={(e) => setIncludeFollowUpAnswers(e.target.checked)}
                disabled={isTranslating || isSending}
                style={{ marginRight: '8px', position: 'relative', top: '2px' }}
              />
              <span style={{ fontWeight: 'bold' }}>Include Follow-Up Form Answers</span>
            </label>
          </div>

          {/* Follow-Up Answers Preview */}
          {includeFollowUpAnswers && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                Follow-Up Form Answers Preview
              </div>
              {isLoadingFollowUpAnswers ? (
                <div style={{ padding: '10px', color: '#666' }}>Loading answers...</div>
              ) : followUpAnswers ? (
                <div
                  className="o-textarea"
                  style={{
                    backgroundColor: '#f5f5f5',
                    width: '100%',
                    whiteSpace: 'pre-wrap',
                    minHeight: '50px',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                >
                  {followUpAnswers}
                </div>
              ) : (
                <div style={{ padding: '10px', color: '#666', fontStyle: 'italic' }}>
                  No follow-up answers available.
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '20px',
              flexWrap: 'wrap',
            }}
          >
            {/* Translate button with language selector */}
            <div>
              <div
                className="c-locale-legend"
                style={{ visibility: targetTranslateLocale === defaultLocale ? 'visible' : 'hidden' }}
              >
                <span role="img" aria-label="check">✅</span> Account&apos;s default language
              </div>
              <div
                className="c-locale-legend"
                style={{ color: '#666', visibility: targetTranslateLocale === locale ? 'visible' : 'hidden' }}
              >
                Your message is already in {localeNames[locale]}
              </div>
              <div className="u-flex" style={{ marginTop: '10px' }}>
                <button
                  onClick={() => translateMessage(targetTranslateLocale)}
                  className="o-sp-button o-sp-button--base o-sp-button--translate o-sp-button--prefixed"
                  disabled={!message || isTranslating || isSending || targetTranslateLocale === locale}
                  style={{ height: '40px', borderRight: 0 }}
                >
                  {isTranslating ? 'Translating...' : 'Translate to'}
                </button>
                <div>
                  <Select
                    value={targetTranslateLocale}
                    options={localeOptions}
                    onChange={(opt) => setTargetTranslateLocale(opt.value)}
                    clearable={false}
                    disabled={!selectedMessage || isTranslating || isSending}
                    className="o-select--suffixed o-select--top"
                    optionRenderer={localeOptionRenderer}
                    valueRenderer={localeOptionRenderer}
                  />
                </div>
              </div>
            </div>

            {/* Send button */}
            <button
              onClick={send}
              className="o-sp-button o-sp-button--base o-sp-button--primary"
              disabled={isSending || isTranslating || !selectedMessage}
              style={{ height: '40px' }}
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

FollowUpParentMessage.defaultProps = {
  defaultLocale: 'en',
  survey: null,
  onSent: () => {},
  onTranslationStateChange: () => {},
};

FollowUpParentMessage.propTypes = {
  defaultLocale: PropTypes.string,
  survey: PropTypes.object.isRequired,
  onSent: PropTypes.func,
  onTranslationStateChange: PropTypes.func,
};
